import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Button from 'reactstrap/lib/Button';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Input from 'reactstrap/lib/Input';
import InputGroup from 'reactstrap/lib/InputGroup';
import Label from 'reactstrap/lib/Label';
import InputGroupText from 'reactstrap/lib/InputGroupText';
import { isString, noop, isNumber } from 'lodash-es';
import classNames from 'classnames';
import { TooltipItem } from 'site-modules/shared/components/tooltip-item/tooltip-item';
import { NotificationMessage } from 'site-modules/shared/components/calculator/notification-message/notification-message';
import { NEGATIVE_VALIDATION_MESSAGE, VEHICLE_PRICE } from 'client/site-modules/shared/constants/calculator/calculator';

import './payment-input.scss';

/**
 * PaymentInput component
 */
export class PaymentInput extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    inputRef: PropTypes.shape({ current: PropTypes.object }),
    inputMode: PropTypes.string,
    label: PropTypes.string,
    tooltip: PropTypes.shape({
      text: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
      placement: PropTypes.string,
    }),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    forceValue: PropTypes.number,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onKeyPress: PropTypes.func,
    isCurrency: PropTypes.bool,
    isPercentage: PropTypes.bool,
    inputAriaLabel: PropTypes.string,
    trackingId: PropTypes.string,
    leftColClasses: PropTypes.string,
    rightColClasses: PropTypes.string,
    tooltipClassName: PropTypes.string,
    tooltipIconClassName: PropTypes.string,
    tooltipInnerClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    debounce: PropTypes.number,
    leftColSizes: PropTypes.shape({
      xs: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
    }),
    rightColSizes: PropTypes.shape({
      xs: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
    }),
    limitMessage: PropTypes.string,
    appraisalMMYLabel: PropTypes.string,
    disallowNegative: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    infoMsg: PropTypes.string,
    paymentInputClassNames: PropTypes.string,
    childrenClassName: PropTypes.string,
    errorMessage: PropTypes.string,
    inputType: PropTypes.string,
    enableResetLink: PropTypes.bool,
    resetVehiclePrice: PropTypes.func,
    tooltipAutoHide: PropTypes.bool,
    tooltipDelay: PropTypes.shape({ show: PropTypes.number, hide: PropTypes.number }),
    screenReaderTooltipLabel: PropTypes.string,
  };

  static defaultProps = {
    inputRef: undefined,
    value: null,
    forceValue: null,
    isCurrency: false,
    isPercentage: false,
    inputAriaLabel: null,
    children: null,
    debounce: 2000,
    leftColSizes: { xs: 8, md: 8, lg: 9 },
    rightColSizes: { xs: 4, md: 4, lg: 3 },
    leftColClasses: 'pe-0 pe-md-0_5',
    rightColClasses: 'ps-0',
    inputClassName: 'text-gray-darker',
    tooltip: null,
    tooltipClassName: '',
    tooltipIconClassName: '',
    tooltipInnerClassName: '',
    labelClassName: '',
    label: '',
    inputMode: 'numeric',
    className: '',
    trackingId: '',
    limitMessage: '',
    appraisalMMYLabel: '',
    disallowNegative: false,
    disabled: false,
    readOnly: false,
    onKeyPress: noop,
    onBlur: noop,
    onChange: null,
    infoMsg: '',
    paymentInputClassNames: '',
    childrenClassName: 'ps-1_5',
    errorMessage: '',
    inputType: 'number',
    enableResetLink: false,
    resetVehiclePrice: noop,
    tooltipAutoHide: undefined,
    tooltipDelay: undefined,
    screenReaderTooltipLabel: '',
  };

  state = {
    negativeValidationMessage: '',
  };

  onKeyPress = e => {
    const { onKeyPress } = this.props;
    const { negativeValidationMessage } = this.state;
    if (!negativeValidationMessage) {
      onKeyPress(e);
    }
  };

  handleFocus = e => {
    e.target.select();
  };

  handleBlur = e => {
    const { disallowNegative, label } = this.props;

    const targetValue = e.target.value;
    const value = isString(targetValue) ? targetValue.replace(/^0+\B|,/, '') : targetValue;

    const negativeValidationMessage =
      (disallowNegative && value < 0) || isNaN(value) ? NEGATIVE_VALIDATION_MESSAGE(label) : '';

    if (!negativeValidationMessage) this.props.onBlur(e);

    this.setState({ negativeValidationMessage });
  };

  /**
   * Renders column with label and passed children.
   * @returns {ReactElement}
   */
  renderLabel = () => {
    const {
      name,
      label,
      children,
      labelClassName,
      leftColSizes,
      leftColClasses,
      tooltip,
      tooltipClassName,
      tooltipIconClassName,
      tooltipInnerClassName,
      tooltipAutoHide,
      isCurrency,
      appraisalMMYLabel,
      childrenClassName,
      tooltipDelay,
      screenReaderTooltipLabel,
    } = this.props;

    if (!label && !children) return null;

    return (
      <Col {...leftColSizes} className={leftColClasses}>
        {label && (
          <Fragment>
            <Label className={classNames('mb-0', labelClassName, { 'me-0_25 me-md-0_5': !tooltip })} for={name}>
              <span className="me-md-0_25">{label}</span>
            </Label>
            {tooltip && (
              <TooltipItem
                id={name}
                item={tooltip}
                className={classNames('me-0_25 me-md-0_5', tooltipClassName)}
                iconClassName={tooltipIconClassName}
                innerClassName={tooltipInnerClassName}
                autohide={tooltipAutoHide}
                delay={tooltipDelay}
                screenReaderTooltipLabel={screenReaderTooltipLabel}
              />
            )}
            {appraisalMMYLabel && <div className="small text-gray-darker">{appraisalMMYLabel}</div>}
          </Fragment>
        )}
        {isCurrency ? <div className={childrenClassName}>{children}</div> : children}
      </Col>
    );
  };

  render() {
    const {
      name,
      onChange,
      inputRef,
      inputMode,
      isCurrency,
      isPercentage,
      inputAriaLabel,
      trackingId,
      rightColSizes,
      rightColClasses,
      inputClassName,
      className,
      limitMessage,
      disallowNegative,
      infoMsg,
      value,
      forceValue,
      paymentInputClassNames,
      disabled,
      readOnly,
      errorMessage,
      inputType,
      enableResetLink,
      resetVehiclePrice,
      label,
    } = this.props;
    const { negativeValidationMessage } = this.state;

    let ariaLabel;

    if (inputAriaLabel) {
      if (isCurrency) ariaLabel = `${inputAriaLabel} (Dollar Amount)`;
      else if (isPercentage) ariaLabel = `${inputAriaLabel} (Percentage)`;
      else ariaLabel = inputAriaLabel;
    }

    return (
      <Row className={classNames('calculator-payment-input align-items-center', className)}>
        {this.renderLabel()}
        <Col {...rightColSizes} className={rightColClasses}>
          <InputGroup className={classNames(paymentInputClassNames, { disabled })}>
            {isCurrency && <InputGroupText>$</InputGroupText>}
            <Input
              innerRef={inputRef}
              type={inputType}
              inputMode={inputMode}
              data-tracking-id={trackingId}
              data-tracking-value={value}
              name={name}
              id={name}
              bsSize="sm"
              className={classNames('payment-input ms-0 text-end pe-0_25 ps-0_75', inputClassName)}
              onKeyPress={this.onKeyPress}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              onChange={onChange}
              step={isCurrency ? 100 : null}
              invalid={!!limitMessage || !!negativeValidationMessage || !!errorMessage}
              disabled={disabled}
              readOnly={readOnly}
              {...(isNumber(forceValue) ? { value: forceValue } : { defaultValue: value })}
              {...(disallowNegative ? { min: 0 } : {})}
              aria-label={ariaLabel}
            />
            {isPercentage && <InputGroupText>%</InputGroupText>}
          </InputGroup>
          {label === VEHICLE_PRICE && (
            <div className="reset-link text-end">
              {enableResetLink && (
                <Button
                  color="link"
                  className="d-inline-block small text-primary-darker form-link p-0 reset"
                  onClick={resetVehiclePrice}
                >
                  Reset
                </Button>
              )}
            </div>
          )}
        </Col>
        {!!limitMessage && <NotificationMessage level="info">{limitMessage}</NotificationMessage>}
        {!!negativeValidationMessage && <NotificationMessage>{negativeValidationMessage}</NotificationMessage>}
        {!!infoMsg && <NotificationMessage level="info">{infoMsg}</NotificationMessage>}
        {!!errorMessage && <NotificationMessage>{errorMessage}</NotificationMessage>}
      </Row>
    );
  }
}
